import { useVirtualizer } from '@tanstack/react-virtual';
import { useLayoutEffect, useRef } from 'react';

import { TABLE_ROW_HEIGHT_PX } from '@/config/constants';

import { DATE_COLUMN_WIDTH, DEFAULT_VIRTUAL_RECT } from './constants';
import { buildFallbackVirtualColumns } from './spotHistoryColumn';
import type { VirtualCell } from './types';

type Params = {
  columnCount: number;
  displayRowCount: number;
  dataColumnWidths: number[];
  headerHeight: number;
  maxHeight: number;
};

function computeVirtualRows(measured: VirtualCell[], displayRowCount: number, maxHeight: number) {
  if (measured.length) return measured;
  const fallbackCount = Math.min(displayRowCount, Math.ceil(maxHeight / TABLE_ROW_HEIGHT_PX) + 10);
  return Array.from({ length: fallbackCount }, (_, index) => ({
    index,
    size: TABLE_ROW_HEIGHT_PX,
    start: index * TABLE_ROW_HEIGHT_PX
  }));
}

export function useSpotHistoryVirtualizers({
  columnCount,
  displayRowCount,
  dataColumnWidths,
  headerHeight,
  maxHeight
}: Params) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual powers the large spot history grid.
  const rowVirtualizer = useVirtualizer({
    count: displayRowCount,
    estimateSize: () => TABLE_ROW_HEIGHT_PX,
    getScrollElement: () => scrollRef.current,
    initialRect: { ...DEFAULT_VIRTUAL_RECT, height: maxHeight },
    overscan: 10,
    scrollMargin: headerHeight
  });

  const columnVirtualizer = useVirtualizer({
    count: columnCount + 1,
    estimateSize: index => (index === 0 ? DATE_COLUMN_WIDTH : dataColumnWidths[index - 1]),
    getScrollElement: () => scrollRef.current,
    horizontal: true,
    initialRect: { ...DEFAULT_VIRTUAL_RECT, height: maxHeight },
    overscan: 2
  });

  useLayoutEffect(() => {
    columnVirtualizer.measure();
  }, [columnVirtualizer, dataColumnWidths]);

  const measuredVirtualColumns = columnVirtualizer
    .getVirtualItems()
    .filter(virtualColumn => virtualColumn.index > 0);
  const measuredVirtualRows = rowVirtualizer.getVirtualItems();
  const virtualRows = measuredVirtualRows.length
    ? measuredVirtualRows.map(virtualRow => ({
        ...virtualRow,
        start: virtualRow.start - headerHeight
      }))
    : computeVirtualRows(measuredVirtualRows, displayRowCount, maxHeight);
  const virtualColumns: VirtualCell[] = measuredVirtualColumns.length
    ? measuredVirtualColumns
    : buildFallbackVirtualColumns(dataColumnWidths);
  const totalWidth =
    columnVirtualizer.getTotalSize() ||
    DATE_COLUMN_WIDTH + dataColumnWidths.reduce((sum, width) => sum + width, 0);
  const bodyHeight = rowVirtualizer.getTotalSize() || displayRowCount * TABLE_ROW_HEIGHT_PX;

  return { scrollRef, virtualColumns, virtualRows, totalWidth, bodyHeight };
}
